import React from 'react';

interface AdminStatusToggleProps {
    isActive: boolean;
    onChange: (isActive: boolean) => void;
}

const AdminStatusToggle: React.FC<AdminStatusToggleProps> = ({ isActive, onChange }) => {
    return (
        <div className="relative bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-5 py-2 sm:py-3 md:py-4 border border-white/30 shadow-lg w-full sm:w-auto ring-1 ring-white/20">
            {/* Admin Badge */}
            <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-900 text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow-lg border border-amber-300 animate-pulse">
                <div className="flex items-center gap-0.5 sm:gap-1">
                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12a7.971 7.971 0 00-1.343-4.243 1 1 0 010-1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M13.828 7.172a1 1 0 011.414 0A5.983 5.983 0 0117 12a5.983 5.983 0 01-1.758 4.828 1 1 0 11-1.414-1.414A3.987 3.987 0 0015 12a3.987 3.987 0 00-1.172-2.828 1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden xs:inline sm:inline">ADMIN</span>
                    <span className="inline xs:hidden sm:hidden">A</span>
                </div>
            </div>
            
            {/* Admin Feature Header */}
            <div className="mb-2 sm:mb-3 pb-1.5 sm:pb-2 border-b border-white/20">
                <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-semibold leading-tight">
                        <span className="hidden sm:inline">สิทธิเฉพาะ Admin</span>
                        <span className="sm:hidden">Admin เท่านั้น</span>
                    </span>
                </div>
            </div>
            
            <div className="flex items-center justify-between gap-2 w-full">
                {/* Left side: Toggle and Text */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative">
                        <input
                            type="checkbox"
                            id="activeStatusToggle"
                            checked={isActive}
                            onChange={(e) => onChange(e.target.checked)}
                            className="sr-only"
                        />
                        <label
                            htmlFor="activeStatusToggle"
                            className={`flex items-center cursor-pointer transition-all duration-300 ${isActive ? 'text-green-100' : 'text-white/80'
                                }`}
                        >
                            <div className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors duration-300 ${isActive
                                ? 'bg-green-500 shadow-green-500/30'
                                : 'bg-white/30'
                                } shadow-lg`}>
                                <span
                                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform duration-300 shadow-lg ${isActive ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                                        }`}
                                />
                            </div>
                        </label>
                    </div>
                    
                    <span className="text-xs sm:text-sm font-medium text-white leading-tight flex-shrink-0">
                        <span className="hidden sm:inline">สร้างแม่พิมพ์ในสถานะ</span>
                        <span className="sm:hidden">สร้างในสถานะ</span>
                    </span>
                </div>
                
                {/* Center: Status Badge */}
                <div className="flex items-center justify-center flex-1 px-2">
                    <div className={`inline-flex items-center px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-300 ${isActive
                        ? 'bg-green-500/20 text-green-100 border-green-400/50'
                        : 'bg-orange-500/20 text-orange-100 border-orange-400/50'
                        }`}>
                        <span className={`w-1.5 h-1.5 mr-1.5 sm:mr-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-orange-400'
                            }`} />
                        <p className="text-xs leading-tight font-medium whitespace-nowrap">{isActive ? 'พร้อมใช้งาน' : 'ไม่พร้อมใช้งาน'}</p>
                    </div>
                </div>
                
                {/* Right side: Info Icon */}
                <div className="relative group flex-shrink-0">
                    <div className="hidden sm:flex w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center cursor-help transition-colors duration-200">
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute top-6 right-0 sm:top-8 sm:right-0 bg-gray-900/95 text-white text-xs rounded-lg px-3 py-2 w-64 sm:w-auto sm:whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-xl border border-gray-700">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
                                    <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="font-semibold text-amber-300 text-xs sm:text-sm">
                                    <span className="sm:hidden">Admin เท่านั้น</span>
                                    <span className="hidden sm:inline">สิทธิเฉพาะ Admin เท่านั้น</span>
                                </div>
                            </div>
                            <div className="pl-1 sm:pl-2 border-l-2 border-amber-400/50 space-y-1">
                                <div className="text-xs font-medium text-green-300">
                                    <span className="sm:hidden">✓ เปิด: พร้อมใช้งานทันที</span>
                                    <span className="hidden sm:inline">✓ เปิด: สร้างแม่พิมพ์ในสถานะพร้อมใช้งานทันที</span>
                                </div>
                                <div className="text-xs font-medium text-orange-300">
                                    <span className="sm:hidden">⚠ ปิด: ไม่พร้อมใช้งาน</span>
                                    <span className="hidden sm:inline">⚠ ปิด: สร้างแม่พิมพ์ในสถานะไม่พร้อมใช้งาน (ปกติ)</span>
                                </div>
                                <div className="text-xs text-gray-300 mt-1 pt-1 border-t border-gray-600">
                                    <span className="sm:hidden">ผู้ใช้ทั่วไปสร้างแม่พิมพ์ในสถานะไม่พร้อมใช้งานเสมอ</span>
                                    <span className="hidden sm:inline">ผู้ใช้ทั่วไปจะสร้างแม่พิมพ์ในสถานะไม่พร้อมใช้งานเสมอ</span>
                                </div>
                            </div>
                        </div>
                        {/* Arrow */}
                        <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 transform rotate-45 border-l border-t border-gray-700"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminStatusToggle;
